/* ===================================================================
   Typed HTTP client. Talks to the backend at VITE_API_BASE_URL or
   same-origin /api. Most helpers fail soft on network errors — callers
   receive `null` and the app continues in-memory — but persistence
   failures are broadcast via onPersistError so the UI can warn.
=================================================================== */

const ENV_BASE = (import.meta.env && (import.meta.env as any).VITE_API_BASE_URL) || "";
export const BASE = (ENV_BASE ? ENV_BASE.replace(/\/+$/, "") : "") + "/api";

/* ---- persistence-failure notifications (B2) ---- */
type PersistListener = (msg: string | null) => void;
const persistListeners = new Set<PersistListener>();
export function onPersistError(fn: PersistListener): () => void {
  persistListeners.add(fn);
  return () => persistListeners.delete(fn);
}
export function notifyPersistError(msg: string | null): void {
  persistListeners.forEach((fn) => fn(msg));
}

export class ApiError extends Error {
  status: number;
  body?: string;
  constructor(status: number, message: string, body?: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = BASE + path;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) || {}),
  };
  const r = await fetch(url, { ...init, headers });
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new ApiError(r.status, `HTTP ${r.status} on ${path}`, body);
  }
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) return (await r.json()) as T;
  return (await r.text()) as unknown as T;
}

export async function safeFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    return await apiFetch<T>(path, init);
  } catch {
    return null;
  }
}

/* ---- typed persistence (replaces the legacy /state blob store) ---- */

export interface BootstrapResponse {
  initialized: boolean;
  corpuses: unknown[];
  evalSets: unknown[];
  wsBundles: unknown[];
  checkVersions: unknown[];
  workspaces: unknown[];
  checkDraft: unknown | null;
  ui: Record<string, unknown> | null;
  runStates: Record<string, unknown>;
}

export async function fetchBootstrap(): Promise<BootstrapResponse | null> {
  return safeFetch<BootstrapResponse>("/bootstrap");
}

/* ---- latest persisted result per check (registry fired examples) ---- */

export interface LatestCheckResult {
  emailSubject: string;
  emailFrom: string;
  verdict: string;
  confidence: number;
  inputSummary: string | null;
  rationale: string | null;
  resultJson: unknown;
  model: string;
  tokensIn: number;
  tokensOut: number;
  cost: number;
  runAt: string;
}

export async function fetchLatestCheckResults(): Promise<Record<string, LatestCheckResult>> {
  const r = await safeFetch<{ results: Record<string, LatestCheckResult> }>(
    "/checks/latest-results",
  );
  return (r && r.results) || {};
}

async function persist(path: string, init: RequestInit, what: string): Promise<unknown> {
  try {
    const r = await apiFetch(path, init);
    notifyPersistError(null);
    return r;
  } catch (e: any) {
    notifyPersistError(`Failed to save ${what} — ${e?.message || "server unreachable"}. Recent changes are not persisted.`);
    return null;
  }
}

export type CollectionName = "eval-sets" | "ws-bundles" | "check-versions" | "workspaces";

export function saveCollection(name: CollectionName, docs: unknown[]): Promise<unknown> {
  return persist(`/collections/${name}`, { method: "PUT", body: JSON.stringify(docs) }, name);
}

export function saveUiState(
  key: "ui" | "checkDraft" | "meta" | "agentGen",
  value: unknown,
): Promise<unknown> {
  return persist(`/ui-state/${key}`, { method: "PUT", body: JSON.stringify(value) }, key);
}

/* Read a single ui-state value (used by the Settings view for agent-gen config). */
export async function fetchUiState<T>(key: "agentGen"): Promise<T | null> {
  const r = await safeFetch<{ value: T | null }>(`/ui-state/${encodeURIComponent(key)}`);
  return r?.value ?? null;
}

export function saveRunState(workspaceId: string, snapshot: unknown): Promise<unknown> {
  return persist(
    `/workspaces/${encodeURIComponent(workspaceId)}/run-state`,
    { method: "PUT", body: JSON.stringify(snapshot) },
    "run state",
  );
}

export function deleteRunState(workspaceId: string): Promise<unknown> {
  return persist(
    `/workspaces/${encodeURIComponent(workspaceId)}/run-state`,
    { method: "DELETE" },
    "run state",
  );
}

export function saveMatterMeta(matterId: string, meta: unknown): Promise<unknown> {
  return persist(
    `/matters/${encodeURIComponent(matterId)}/meta`,
    { method: "PUT", body: JSON.stringify(meta) },
    "corpus details",
  );
}

/* Debounce arbitrary persistence work under a string key. */
const timers: Record<string, ReturnType<typeof setTimeout>> = {};
export function debounced(key: string, fn: () => void, delay = 400): void {
  if (timers[key]) clearTimeout(timers[key]);
  timers[key] = setTimeout(() => {
    delete timers[key];
    fn();
  }, delay);
}
export function cancelDebounced(key: string): void {
  if (timers[key]) {
    clearTimeout(timers[key]);
    delete timers[key];
  }
}

/* Extract the server's error message from an ApiError body ({error: "..."}). */
function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    try {
      const j = JSON.parse(e.body || "");
      if (j && typeof j.error === "string") return j.error;
    } catch { /* not JSON */ }
    return e.message;
  }
  return (e as Error)?.message || "backend unreachable";
}

/* ---- OpenRouter model catalog (proxied via the API, cached server-side) ---- */
export interface OpenRouterModel {
  id: string;
  name: string;
  /** USD per million input / output tokens */
  inP: number;
  outP: number;
}

export function fetchOpenRouterModels(): Promise<OpenRouterModel[] | null> {
  return safeFetch<OpenRouterModel[]>("/models");
}

export interface ExtractArgs {
  content: string;
  targetType: "corpus" | "eval" | "workstream";
  schema?: unknown;
}

export interface ExtractResponse {
  ok: boolean;
  data?: unknown;
  error?: string;
}

export async function extractFromContent(args: ExtractArgs): Promise<ExtractResponse> {
  try {
    return await apiFetch<ExtractResponse>("/extract", {
      method: "POST",
      body: JSON.stringify(args),
    });
  } catch (e) {
    return { ok: false, error: apiErrorMessage(e) };
  }
}
